<template>
  <div :class="$style.container">
    <LoadingOverlay
      :showLoadingOverlay="isLoading"
    />
    <div :class="$style.tabsContainer">
      <Tabs
        :activeTab="activeTabName"
        :class="$style.tabs"
        :isRounded="true"
        :tabs="tabs"
        @selectedTab="handleSelectedTab"
      />
    </div>
    <div v-if="devicesExist && !isLoading">
      <SystemFleetDistributionCard
        :activeTabIndex="activeTabIndex"
        :activeTabName="activeTabName"
        :lastContactSum="lastContactSum"
        :osListFormatted="osListFormatted"
        :supportedOSes="supportedOSes"
        :tabs="tabs"
        @changeTab="handleChangeTab"
        @changeTimeFrame="changeTimeFrame"
      />
      <div
        :class="$style.secondRowContainer"
      >
        <OsEventsGraph
          v-if="shouldShowOsEventsGraph"
          :class="$style.leftBlock"
          :timeFrame="timeFrame"
        />
        <OsReleaseTrainsCard
          v-else
          :class="$style.leftBlock"
          :showTabs="false"
          :tabSelected="fleetDistributionTab"
        />
        <OsPatchPoliciesCard
          :class="$style.osPatchPoliciesCard"
        />
      </div>
    </div>
    <GettingStartedPanel
      :customGettingStartedGraphic="gettingStartedGraphic"
      :itemType="itemTypeDevicesHome"
      :visible="!devicesExist && !isLoading"
    />
  </div>
</template>

<script>
import {
  DeviceSupportedOSLabels,
  GettingStartedRootLink,
  ItemTypeConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import { Tabs, Types } from '@jumpcloud/ui-components';
import { mapActions, mapState } from 'vuex';
import GettingStartedPanel from 'client/components/GettingStartedPanel/GettingStartedPanel.vue';
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay.vue';
import OsEventsGraph from 'client/systems/OsEventsGraph';
import OsPatchPoliciesCard from 'client/systems/OsPatchPoliciesCard';
import OsReleaseTrainsCard from 'client/systems/OsReleaseTrainsCard';
import SystemFleetDistributionCard from 'client/systems/SystemFleetDistributionCard';

const { device } = ItemTypeConsts;
const { TabItem } = Types;
const { windows, linux, macos } = DeviceSupportedOSLabels;

const systemCollection = StoreModuleMap[ItemTypeConsts.system];

export default {
  name: 'DevicesHomeMainPanel',

  components: {
    GettingStartedPanel,
    LoadingOverlay,
    OsEventsGraph,
    OsPatchPoliciesCard,
    OsReleaseTrainsCard,
    SystemFleetDistributionCard,
    Tabs,
  },

  data() {
    return {
      activeTabName: 'activity',
      itemTypeDevicesHome: device,
      lastContactSum: [],
      isLoading: true,
      totalCount: null,
      timeFrame: 7, // 7 days is the default value
      fleetDistributionTab: '',
    };
  },

  computed: {
    ...mapState('OsDistributionModule', ['osList', 'sum']),

    activeTabIndex: {
      get() {
        let tabIndex = this.tabs.findIndex(os => this.activeTabName.includes(os.label));

        if (tabIndex < 0) {
          tabIndex = 0;
        }

        return this.activeTabName
          ? tabIndex
          : 0;
      },
      set(newActiveTabIndex) {
        // when there's a new value for the 'activeTabIndex', change the 'activeTabName' value
        // this way, 'get' method will recompute the value for 'activeTabIndex' computed prop
        if (this.tabs.length) {
          if (this.tabs[newActiveTabIndex]) {
            this.activeTabName = this.tabs[newActiveTabIndex].key;
          } else {
            this.activeTabName = '';
          }
        } else {
          this.activeTabName = '';
        }
      },
    },

    devicesExist() {
      return this.totalCount > 0;
    },

    gettingStartedGraphic() {
      return {
        component: 'img',
        src: `${GettingStartedRootLink.url}/getting-started-systems-home.svg`,
      };
    },

    osListEmpty() {
      return this.osList.length === 0;
    },

    osListFormatted() {
      return this.supportedOSes.map(os => this.osList.find((system) => system.name === os))
        .filter(obj => obj !== undefined);
    },

    shouldShowOsEventsGraph() {
      return this.fleetDistributionTab === '';
    },

    supportedOSes() {
      return [macos, windows, linux];
    },

    tabs() {
      let tabs = [{ name: 'activity', label: 'Activity' }];

      if (this.osListEmpty) {
        tabs = [...tabs, ...this.zeroStateDefaultTabs];
      } else {
        const newTabs = this.osListFormatted.map(os => ({
          name: os.name + this.timeFrame,
          label: os.name,
        }));

        tabs = [...tabs, ...newTabs];
      }

      return tabs.map((tab) => TabItem({
        key: tab.name,
        label: tab.label,
        disabled: tab.disabled,
      }));
    },

    zeroStateDefaultTabs() {
      const defaultTabs = [
        {
          name: '_Mac',
          label: 'Mac',
          disabled: true,
        },
        {
          name: '_Windows',
          label: 'Windows',
          disabled: true,
        },
      ];

      return [...defaultTabs, { name: '_Linux', label: 'Linux', disabled: true }];
    },
  },

  created() {
    this.handleCreated();
  },

  methods: {
    ...mapActions(systemCollection, ['fetchTotalCount']),
    ...mapActions('OsDistributionModule', ['fetchDistributionLastContact']),
    ...mapActions('OsReleasesPatchesModule', ['fetchOsReleases']),

    changeTimeFrame(val) {
      this.timeFrame = val;
    },

    /**
     * Fetches the OS distribution fleet and sets the sum for the 7 and 30 days filter so that
     * <SystemFleetDistributionCard> has the data when it's mounted, avoiding the bad UX of
     * the chart going to zero state and then load the data a split second after.
     */
    async fetchDonutChartData() {
      await this.fetchDistributionLastContact(7);
      this.lastContactSum[0] = this.sum;
      if (this.sum) return;

      await this.fetchDistributionLastContact(30);
      this.lastContactSum[1] = this.sum;
    },

    async getDevicesTotalCount() {
      await this.fetchTotalCount().then((count) => {
        this.totalCount = count;
      });
    },

    handleChangeTab(tab) {
      this.activeTabIndex = this.tabs.findIndex(({ key }) => key === tab.key);
    },

    async handleCreated() {
      await Promise.allSettled([
        this.getDevicesTotalCount(),
        this.fetchDonutChartData(),
        this.fetchOsReleases(),
      ]);
      this.isLoading = false;
    },

    handleSelectedTab(tab) {
      this.activeTabName = tab;
    },

  },
};
</script>

<style module>
.activeTab a {
  background: var(--jcWhite50);
  border: 2px solid var(--jcPrimary);
  color: var(--jcGrey600);
}

.leftBlock {
  flex: 2;
}

.osPatchPoliciesCard {
  flex: 1;
}

.secondRowContainer {
  display: flex;
}

.tabsContainer {
  background: var(--jcWhite);
  padding: 1rem 0;
}

.tabs {
  margin: 0 1.5rem !important;
}
</style>
